import { LoadingSpinner } from "@connected-repo/ui-mui/components/LoadingSpinner";
import { Box } from "@connected-repo/ui-mui/layout/Box";
import { CustomErrorBoundary } from "@frontend/components/error_fallback";
import { AppLayout } from "@frontend/components/layout/AppLayout";
import { RootLayout } from "@frontend/components/layout/RootLayout";
import { authLoader } from "@frontend/utils/auth.loader";
import * as Sentry from "@sentry/react";
import { createBrowserRouter, type RouteObject, redirect } from "react-router";

type NavbarFields = {
	nb_icon?: string;
};

type BaseRouterWithNavbar = RouteObject & NavbarFields;
type ReactRouterWithNavbar = BaseRouterWithNavbar & {
	children?: ReactRouterWithNavbar[];
};

// HydrateFallback component for initial app loading
const HydrateFallback = () => (
	<Box
		sx={{
			display: "flex",
			alignItems: "center",
			justifyContent: "center",
			minHeight: "100vh",
			bgcolor: "background.default",
		}}
	>
		<LoadingSpinner size={48} />
	</Box>
);

// react-router `lazy` route property: the router awaits the chunk before
// swapping the page, so `useNavigation().state` is "loading" during the fetch
// (which drives the TopProgressBar) instead of React.lazy's blank-Suspense flash.
const lazyRoute = (importer: () => Promise<{ default: React.ComponentType }>) =>
	async () => {
		const mod = await importer();
		return { Component: mod.default };
	};

const routerObjectWithNavbar: ReactRouterWithNavbar[] = [
	{
		path: "/",
		Component: RootLayout,
		errorElement: <CustomErrorBoundary />,
		hydrateFallbackElement: <HydrateFallback />,
		children: [
			{
				index: true,
				loader: () => redirect("/customers"),
			},
			{
				path: "auth/*",
				lazy: lazyRoute(() => import("@frontend/modules/auth/auth.router")),
			},
			// Authenticated routes with AppLayout
			{
				element: <AppLayout />,
				loader: authLoader,
				// The studio keeps its state in the query string: changing only it
				// must not re-run the session check. An explicit revalidate() (same
				// URL, e.g. after saving a Profile setting) still re-reads the session.
				shouldRevalidate: ({ currentUrl, nextUrl, defaultShouldRevalidate }) =>
					currentUrl.pathname === nextUrl.pathname && currentUrl.search !== nextUrl.search
						? false
						: defaultShouldRevalidate,
				children: [
					{
						path: "tryon/customers",
						loader: () => redirect("/customers"),
					},
					{
						path: "tryon/*",
						lazy: lazyRoute(() => import("@frontend/modules/tryon/tryon.router")),
					},
					{
						path: "customers",
						lazy: lazyRoute(() => import("@frontend/modules/tryon/pages/Customers.page")),
					},
					{
						path: "dashboard",
						lazy: lazyRoute(() => import("@frontend/modules/dashboard/pages/Dashboard.page")),
					},
					{
						path: "admin/*",
						lazy: lazyRoute(() => import("@frontend/modules/admin/admin.router")),
					},
					{
						path: "profile",
						lazy: lazyRoute(() => import("@frontend/pages/Profile.page")),
					},
					{
						// The sync status page is gone (no offline-first UI).
						path: "settings/sync",
						loader: () => redirect("/profile"),
					},
				],
			},
		],
	},
];

// Call this AFTER Sentry.init()
const sentryCreateBrowserRouter = Sentry.wrapCreateBrowserRouterV7(
  createBrowserRouter,
);

export const router = sentryCreateBrowserRouter(routerObjectWithNavbar);
